import React, { useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';

import PageHeader from '../../../sharedModules/layout/PageHeader';
import useProjectsData from '../../hooks/useProjectsData';
import { CREW_ASSIGNMENTS, TECHNICIANS } from '../../data/projectsData';
import { TODAY, departmentColor, jobById } from '../../utils/projectsUtils';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Capacity colors, mirroring a typical installer capacity calendar.
const GREEN = '#e8f5e9';
const YELLOW = '#fff8e1';
const RED = '#ffebee';

const ProjectMonthlyCalendarPage: React.FunctionComponent = () => {
  const { source, loading, error } = useProjectsData();
  const [cursor, setCursor] = useState<Date>(() => startOfMonth(parseISO(TODAY)));
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const totalTechs = TECHNICIANS.length;

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(cursor));
    const end = endOfWeek(endOfMonth(cursor));
    return eachDayOfInterval({ start, end });
  }, [cursor]);

  // Single pass: distinct jobs and techs scheduled per day.
  const { jobsByDate, techsByDate } = useMemo(() => {
    const jobs = new Map<string, Set<string>>();
    const techs = new Map<string, Set<string>>();
    CREW_ASSIGNMENTS.forEach((assignment) => {
      if (!assignment.date) return;
      if (!jobs.has(assignment.date)) jobs.set(assignment.date, new Set());
      if (!techs.has(assignment.date)) techs.set(assignment.date, new Set());
      jobs.get(assignment.date)!.add(assignment.job_id);
      techs.get(assignment.date)!.add(assignment.emp_id);
    });
    return { jobsByDate: jobs, techsByDate: techs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source]);

  // Detail for the clicked day: scheduled jobs (with their crews) + unscheduled technicians.
  const dayDetail = useMemo(() => {
    if (!selectedDate) return null;
    const rows = CREW_ASSIGNMENTS.filter((assignment) => assignment.date === selectedDate);
    const jobMap = new Map<string, { job_id: string; customer: string; dept: string; crew: typeof rows }>();
    rows.forEach((assignment) => {
      const job = jobById(assignment.job_id);
      if (!jobMap.has(assignment.job_id)) {
        jobMap.set(assignment.job_id, {
          job_id: assignment.job_id,
          customer: job?.company || job?.description || assignment.job_id,
          dept: job?.dept_id || '',
          crew: [],
        });
      }
      jobMap.get(assignment.job_id)!.crew.push(assignment);
    });
    const jobs = Array.from(jobMap.values()).sort((a, b) => a.customer.localeCompare(b.customer));
    const scheduledEmpIds = new Set(rows.map((assignment) => assignment.emp_id));
    const availableTechs = TECHNICIANS.filter((tech) => !scheduledEmpIds.has(tech.emp_id));
    return {
      jobs,
      scheduledCount: scheduledEmpIds.size,
      availableTechs,
      totalHrs: rows.reduce((sum, assignment) => sum + assignment.duration, 0),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, source]);

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        mainTitle="Calendar"
        subTitle="Daily crew capacity across all project departments — ServMan Tampa"
        titleIcon={<CalendarMonthIcon />}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn’t load project data from ServMan. Refresh to try again.
        </Alert>
      )}
      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading project data…
        </Typography>
      )}

      {/* Month navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
        <IconButton size="small" onClick={() => setCursor((c) => addMonths(c, -1))}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700} sx={{ minWidth: 180, textAlign: 'center' }}>
          {format(cursor, 'MMMM yyyy')}
        </Typography>
        <IconButton size="small" onClick={() => setCursor((c) => addMonths(c, 1))}>
          <ChevronRightIcon />
        </IconButton>
        <Button size="small" onClick={() => setCursor(startOfMonth(parseISO(TODAY)))}>
          Today
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="body2" color="text.secondary">
          {totalTechs} technicians in the project crew pool
        </Typography>
      </Box>

      <Card variant="outlined" sx={{ overflowX: 'auto' }}>
        <Box sx={{ minWidth: 900 }}>
          {/* Weekday header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {WEEKDAYS.map((weekday) => (
              <Box
                key={weekday}
                sx={{ p: 1, textAlign: 'center', backgroundColor: 'grey.200', borderBottom: 1, borderColor: 'divider' }}
              >
                <Typography variant="caption" fontWeight={700}>
                  {weekday.toUpperCase()}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Day cells */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {days.map((day) => {
              const iso = format(day, 'yyyy-MM-dd');
              const inMonth = isSameMonth(day, cursor);
              const isToday = iso === TODAY;
              const scheduledJobs = jobsByDate.get(iso)?.size || 0;
              const scheduledTechs = techsByDate.get(iso)?.size || 0;
              const availableTechs = Math.max(0, totalTechs - scheduledTechs);
              const ratio = totalTechs > 0 ? availableTechs / totalTechs : 0;

              let background = 'transparent';
              if (inMonth) {
                background = availableTechs === 0 ? RED : ratio < 0.25 ? YELLOW : GREEN;
              }

              return (
                <Box
                  key={iso}
                  onClick={inMonth ? () => setSelectedDate(iso) : undefined}
                  sx={{
                    minHeight: 116,
                    p: 1,
                    borderBottom: 1,
                    borderRight: 1,
                    borderColor: 'divider',
                    backgroundColor: background,
                    opacity: inMonth ? 1 : 0.45,
                    cursor: inMonth ? 'pointer' : 'default',
                    '&:hover': inMonth ? { filter: 'brightness(0.97)' } : undefined,
                  }}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Typography
                      variant="caption"
                      fontWeight={isToday ? 700 : 500}
                      sx={{
                        color: isToday ? 'primary.main' : 'text.secondary',
                        ...(isToday && {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          borderRadius: '50%',
                          width: 20,
                          height: 20,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }),
                      }}
                    >
                      {format(day, 'd')}
                    </Typography>
                  </Box>
                  {inMonth && (
                    <Box sx={{ mt: 0.5 }}>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                        Scheduled Jobs: <strong>{scheduledJobs}</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                        Scheduled Techs: <strong>{scheduledTechs}</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.5 }}>
                        Available Techs: <strong>{availableTechs}</strong>
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Card>

      {/* Capacity legend */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mt: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" color="text.secondary">
          Capacity key:
        </Typography>
        {[
          { color: RED, label: 'No technicians available' },
          { color: YELLOW, label: 'Less than 25% available' },
          { color: GREEN, label: 'More than 25% available' },
        ].map((item) => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{ width: 14, height: 14, borderRadius: 0.5, backgroundColor: item.color, border: 1, borderColor: 'divider' }} />
            <Typography variant="caption" color="text.secondary">
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Dialog open={Boolean(selectedDate)} onClose={() => setSelectedDate(null)} maxWidth="md" fullWidth>
        {selectedDate && dayDetail && (
          <>
            <DialogTitle sx={{ pr: 6 }}>
              {format(parseISO(selectedDate), 'EEEE, MMMM d, yyyy')}
              <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                <Chip size="small" label={`${dayDetail.jobs.length} jobs`} />
                <Chip size="small" color="primary" label={`${dayDetail.scheduledCount} techs scheduled`} />
                <Chip size="small" variant="outlined" label={`${dayDetail.availableTechs.length} available`} />
                <Chip size="small" variant="outlined" label={`${dayDetail.totalHrs.toLocaleString()} hrs`} />
              </Box>
              <IconButton
                onClick={() => setSelectedDate(null)}
                size="small"
                sx={{ position: 'absolute', right: 8, top: 8 }}
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Scheduled jobs ({dayDetail.jobs.length})
              </Typography>
              {dayDetail.jobs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  No crew scheduled on this day.
                </Typography>
              ) : (
                dayDetail.jobs.map((job) => (
                  <Box key={job.job_id} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 0.5, backgroundColor: departmentColor(job.dept) }} />
                      <Typography variant="body2" fontWeight={700}>
                        #{job.job_id} — {job.customer}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {job.crew.length} on crew
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 0.5, ml: 2.5 }}>
                      {job.crew.map((member, index) => (
                        <Chip
                          key={`${member.emp_id}-${index}`}
                          size="small"
                          variant="outlined"
                          label={`${member.tech_name} · ${member.start_time} · ${member.duration}h`}
                        />
                      ))}
                    </Box>
                  </Box>
                ))
              )}

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                Unscheduled technicians ({dayDetail.availableTechs.length})
              </Typography>
              {dayDetail.availableTechs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  All {TECHNICIANS.length} technicians in the crew pool are scheduled this day.
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {dayDetail.availableTechs.map((tech) => (
                    <Chip key={tech.emp_id} size="small" label={tech.name} />
                  ))}
                </Box>
              )}
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default ProjectMonthlyCalendarPage;
